import {
  autoUpdate,
  computePosition,
  flip,
  hide,
  limitShift,
  offset,
  shift,
  size,
  type Placement,
  type VirtualElement,
} from "@floating-ui/dom";
import {
  computed,
  onScopeDispose,
  ref,
  toValue,
  watch,
  type CSSProperties,
  type MaybeRefOrGetter,
  type Ref,
  type ComputedRef,
} from "vue";
import {
  isJust,
  isNothing,
  just,
  nothing,
  matchMaybe,
  type Maybe,
} from "../lib/maybe";

/**
 * Viewport-relative box a panel anchors to. Use this only when there's no real
 * element to point at (a text selection, a caret). Prefer passing the element:
 * a rect is a snapshot, so the caller becomes responsible for handing over a
 * fresh one whenever the anchor moves, while an element is tracked for free.
 */
export type AnchorRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

export type AnchorPayload = HTMLElement | AnchorRect;
export type AnchorSource = Maybe<AnchorPayload>;

/** Never shrink a panel below this; scrolling beats an unusable sliver. */
const MIN_PANEL_HEIGHT = 96;

type Options = {
  /** Preferred side. Flips to the opposite side when it doesn't fit. */
  placement?: MaybeRefOrGetter<Placement>;
  /** Gap between anchor and panel, in px. */
  gap?: number;
  /** Distance kept clear of the viewport edges, in px. */
  padding?: number;
};

function toReference(
  sourceM: AnchorSource,
): Maybe<HTMLElement | VirtualElement> {
  return matchMaybe(sourceM, {
    nothing: () => nothing,
    just: (source) => {
      // A detached element measures 0×0; positioning against that would park the
      // panel in the top-left corner.
      if (source instanceof HTMLElement) {
        return source.isConnected ? just(source) : nothing;
      }
      const { top, left, width, height } = source;
      return just({
        getBoundingClientRect: () => new DOMRect(left, top, width, height),
      });
    },
  });
}

/**
 * Positions a floating panel against an anchor, keeping it inside the viewport:
 * flips to the opposite side when the preferred one doesn't fit, slides along
 * the cross axis to stay in bounds, and caps its size to the space left over.
 *
 * Panels are positioned `fixed` so they're measured against the viewport rather
 * than a clipping ancestor — pair this with a `<Teleport to="body">` so an
 * `overflow: hidden` parent can't cut the panel off.
 */
export function useAnchoredFloating(
  anchor: MaybeRefOrGetter<AnchorSource>,
  floating: Ref<Maybe<HTMLElement>>,
  open: MaybeRefOrGetter<boolean>,
  options: Options = {},
): {
  floatingStyle: ComputedRef<CSSProperties>;
  placement: Ref<Placement, Placement>;
  side: ComputedRef<string>;
  positioned: Ref<boolean, boolean>;
  anchorHidden: Ref<boolean, boolean>;
  update: () => Promise<void>;
} {
  const { gap = 6, padding = 8 } = options;

  const x = ref(0);
  const y = ref(0);
  const maxHeight = ref<Maybe<number>>(nothing);
  const maxWidth = ref<Maybe<number>>(nothing);
  const placement = ref<Placement>("bottom");
  /** False until the first measurement, so the panel never flashes at 0,0. */
  const positioned = ref(false);
  /** True once the anchor has been scrolled out of view. */
  const anchorHidden = ref(false);

  let stopAutoUpdate: Maybe<() => void> = nothing;

  async function update(): Promise<void> {
    const referenceM = toReference(toValue(anchor));
    const panelM = floating.value;
    if (isNothing(referenceM) || isNothing(panelM)) return;
    const reference = referenceM.value;
    const panel = panelM.value;

    const result = await computePosition(reference, panel, {
      strategy: "fixed",
      placement: toValue(options.placement) ?? "bottom",
      middleware: [
        offset(gap),
        // Order matters: flip picks the side, shift slides along the cross
        // axis, then size measures what's left on the side we settled on.
        flip({ padding }),
        shift({ padding, limiter: limitShift() }),
        size({
          padding,
          apply({ availableHeight, availableWidth }): void {
            maxHeight.value = just(
              Math.max(MIN_PANEL_HEIGHT, Math.floor(availableHeight)),
            );
            maxWidth.value = just(Math.floor(availableWidth));
          },
        }),
        // Detect the anchor being scrolled out of view, so a panel doesn't hover
        // over unrelated content after the thing it acts on has left the screen.
        hide({ padding }),
      ],
    });

    x.value = Math.round(result.x);
    y.value = Math.round(result.y);
    placement.value = result.placement;
    anchorHidden.value = result.middlewareData.hide?.referenceHidden === true;
    positioned.value = true;
  }

  function stop(): void {
    if (isJust(stopAutoUpdate)) stopAutoUpdate.value();
    stopAutoUpdate = nothing;
    positioned.value = false;
    anchorHidden.value = false;
  }

  function start(): void {
    stop();
    const referenceM = toReference(toValue(anchor));
    const panelM = floating.value;
    if (isNothing(referenceM) || isNothing(panelM)) return;
    const reference = referenceM.value;
    const panel = panelM.value;
    // autoUpdate covers scroll and element resize; anchor-prop changes come
    // through the watcher below.
    stopAutoUpdate = just(autoUpdate(reference, panel, update));
  }

  watch(
    [() => toValue(open), floating],
    ([isOpen, panelM]) => {
      if (isOpen === true && isJust(panelM)) start();
      else stop();
    },
    { immediate: true, flush: "post" },
  );

  // A virtual anchor is a plain object the observers can't watch — recompute
  // whenever the parent hands us a new rect.
  watch(
    () => toValue(anchor),
    () => {
      if (toValue(open) === false) return;
      if (isJust(stopAutoUpdate)) void update();
      else start();
    },
    { flush: "post" },
  );

  onScopeDispose(stop);

  /**
   * Space is published as custom properties rather than `max-height` directly,
   * so a panel can combine it with its own design ceiling:
   *   max-height: min(20rem, var(--zw-float-max-height, 100svh));
   * An inline `max-height` would override the stylesheet and lose that ceiling.
   */
  const floatingStyle = computed<CSSProperties>(() => ({
    position: "fixed",
    left: `${x.value}px`,
    top: `${y.value}px`,
    ...(isNothing(maxHeight.value)
      ? {}
      : { "--zw-float-max-height": `${maxHeight.value.value}px` }),
    ...(isNothing(maxWidth.value)
      ? {}
      : { "--zw-float-max-width": `${maxWidth.value.value}px` }),
    visibility:
      positioned.value === true && anchorHidden.value === false
        ? "visible"
        : "hidden",
  }));

  /** "top" | "bottom" | "left" | "right" — the side actually used. */
  const side = computed(() => placement.value.split("-")[0] as string);

  return { floatingStyle, placement, side, positioned, anchorHidden, update };
}
